import React, { useState } from "react";
import { useRecoilValue } from "recoil";
import moment from "moment";
import {
  Box,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useStyles from "./data-table.style";
import { MyDocumentsCollapse } from "my-documents/components";
import { myDocumentsIsDocument } from "my-documents/states/is-document-state";
import { myDocumentsShowDocument } from "my-documents/states/show-document-state";
import { MyleaveExaminerDashboardCollapse } from "my-leave/components";
import {
  OcDataTableBodyCell,
  OcDataTableHeaderCell,
  OcDataTableLoadingRows,
  OcDataTablePaginationActions,
  OcDateTableData,
  OcIsMobileTableCollapse,
} from "oc/components";
import {
  useOcCurrentUserState,
  useOcIsMobile,
  useOcLocalState,
} from "oc/hooks";

type DataTableData = {
  rows: any[];
  total?: number;
};

interface OcDataTableProps {
  canCopy?: (row?: any) => any;
  data: DataTableData;
  examinerDasboard?: boolean;
  filteredTableDef?: any;
  handleMoreButton?: any;
  inlineEdit?: boolean;
  inlineEditForm?: React.ReactElement;
  isNotExaminer?: boolean;
  loading?: boolean;
  localPaging?: boolean;
  matchingMyTimeitems?: any;
  myDocuments?: boolean;
  onEditDocument?: (row: any) => void;
  onRowSelection?: (row: any) => void;
  onSelectRow?: (row: any) => void;
  onShowInlineEditForm?: (row: any) => boolean;
  pagination?: boolean;
  removeUpload?: (row: any) => void;
  removeMassUpload?: (row: any) => void;
  role?: any;
  selectedRowId?: string | number;
  setTableData?: (data: any) => void;
  tableData?: any;
  tableDef?: any;
  tableId?: string;
}

export const OcDataTable: React.FC<OcDataTableProps> = ({
  canCopy = () => { },
  data,
  examinerDasboard,
  filteredTableDef,
  handleMoreButton,
  inlineEdit,
  inlineEditForm = <></>,
  isNotExaminer,
  loading,
  localPaging,
  matchingMyTimeitems,
  myDocuments,
  onEditDocument,
  onRowSelection,
  onSelectRow,
  onShowInlineEditForm,
  pagination,
  removeUpload,
  removeMassUpload,
  role,
  selectedRowId,
  setTableData,
  tableData,
  tableDef,
  tableId,
}) => {
  const theme = useTheme();
  const isMobile = useOcIsMobile();
  const classes = useStyles({ select: tableDef?.select });
  let loadingTypes = tableDef?.columns.map((item: any) => {
    return item.type;
  });

  const { employeeCode } = useOcCurrentUserState();
  const tableRowsKey = `${employeeCode}:${tableDef?.id}`;
  const [rowsPerPage, setRowsPerPage] = useOcLocalState(
    tableRowsKey,
    tableData?.pageSize,
  );

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let pageSize = +event.target.value;
    if (pageSize !== tableData?.pageSize) {
      let newState = { ...tableData, page: 1, pageSize };
      if (setTableData) {
        setTableData(newState);
      }
      setRowsPerPage(pageSize);
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    const oneBased = newPage + 1;

    if (oneBased !== tableData?.page && setTableData) {
      setTableData({ page: oneBased });
    }
  };

  const [originalPage, setOriginalPage] = useState(1);

  const handleSearchChange = ({
    searchField,
    searchText,
  }: {
    searchField: string;
    searchText: string;
  }) => {
    const newTableData: OcDateTableData = {
      ...tableData,
      searchField,
      searchText,
    };
    if (searchText !== tableData?.searchText) {
      // If searchText is changed (and filtering is actually happening),
      // go back to the first page.
      if (searchText.length >= 3) {
        newTableData.page = 1;
      }
      // If we just started to type the searchText, remember which page
      // we are on.
      if (searchText !== "" && tableData.searchText === "")
        setOriginalPage(tableData.page);

      if (tableData.searchText !== "" && searchText === "") {
        // If we're no longer searching,
        // go back to the original page.
        newTableData.page = originalPage;
      }
    }
    setTableData!(newTableData);
  };

  if (rowsPerPage !== tableData?.pageSize) {
    let newState = { ...tableData, page: 1, pageSize: rowsPerPage };
    if (setTableData) {
      setTableData(newState);
    }
  }

  let list = data?.rows;

  if (localPaging) {
    let limit = tableData?.pageSize;
    let offset = (tableData?.page - 1) * tableData?.pageSize;
    list = data?.rows.slice(offset, offset + limit);
  }

  const handleRowSelection = (row: any) => {
    if (onRowSelection) {
      onRowSelection(row);
    }
  };

  const dailyTable = tableId === "allLeaveRequestByDayTable";
  const myTimeApprovalTable = tableId === "employeeAdmissionsApprovelTableData";

  const showDocument = useRecoilValue(myDocumentsShowDocument);
  const isDocument = useRecoilValue(myDocumentsIsDocument);

  let disabled = isDocument || showDocument;

  const browserHeight = window.visualViewport?.height ?? window.innerHeight;
  const lessHeight = isMobile ? 300 : 220;

  const computedMaxHeight =
    (dailyTable || myTimeApprovalTable)
      ? browserHeight - lessHeight
      : undefined;

  return (
    <Box
      style={{
        overflow: "visible",
      }}
    >
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          width: "100%",
          ...(computedMaxHeight !== undefined ? { maxHeight: computedMaxHeight } : {}),
          minWidth: tableDef?.minWidth,
          ...(isMobile
            ? { overflowX: "auto", overflowY: "auto" }
            : (data?.rows?.length ?? 0) < 3
              ? { overflow: "visible" }
              : { overflowY: "auto" }),
        }}
      >
        <Table
          stickyHeader
          id={tableDef?.id}
          aria-label="users-table"
          style={{
            tableLayout: isMobile ? "unset" : "fixed",
          }}
        >
          <TableHead style={{ overflowX: "visible" }}>
            <TableRow
              style={{
                overflowX: "visible",
                background: theme.palette.gray30.main,
              }}
            >
              {tableDef.columns.map((item: any, index: number) => {
                let length = tableDef?.columns?.length;
                item.anchor = index > length / 2 ? "right" : "left";
                return (
                  <OcDataTableHeaderCell
                    key={`${tableDef.id}-${item.id}`}
                    tableData={tableData}
                    setTableData={setTableData ?? (() => { })}
                    column={item}
                    dailyTable={dailyTable}
                    tableId={tableId}
                    onSearchChange={handleSearchChange}
                  />
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              list?.map((row, index) => {
                let rowSpanData: any[] = [];
                if (tableDef?.rowSpan && tableDef?.rowSpanField) {
                  const startDate = moment(row?.date).format("YYYY-MM-DD");
                  const filteredData = list?.filter(
                    (item) =>
                      moment(item.date).format("YYYY-MM-DD") === startDate,
                  );
                  rowSpanData = filteredData;
                }
                let showForm = onShowInlineEditForm
                  ? onShowInlineEditForm(row)
                  : false;
                return (
                  <React.Fragment key={`${index}-${row.email}`}>
                    <TableRow
                      id={new Date(row?.date).toLocaleDateString()}
                      onClick={() => {
                        if (tableDef?.select === "row" && onSelectRow)
                          onSelectRow(row);
                      }}
                      className={`${classes.root} ${selectedRowId != null &&
                        (selectedRowId === row.contract ||
                          selectedRowId === row._id)
                        ? classes.selectedRow
                        : ""
                        }`}
                    >
                      {tableDef.columns.map((item: any) => {
                        return (
                          <OcDataTableBodyCell
                            key={`${tableDef.id}-body-cell-${item.id}`}
                            row={row}
                            id={item.id}
                            align={item.align}
                            type={item.type}
                            onSelectRow={onSelectRow ?? (() => { })}
                            selectedRowValue={tableData?.selectedRowValue}
                            inlineEdit={inlineEdit ?? false}
                            canCopy={canCopy(row)}
                            rowSpanData={rowSpanData}
                            rowSpanField={tableDef?.rowSpanField}
                            colorize={item.colorize}
                            isNotExaminer={isNotExaminer ?? false}
                            selectedRowId={
                              selectedRowId !== undefined
                                ? String(selectedRowId)
                                : ""
                            }
                            isMobile={isMobile}
                            filterSelected={tableData?.filterSelected}
                            filterField={tableData?.filterField}
                          />
                        );
                      })}
                    </TableRow>
                    {onShowInlineEditForm && (
                      <TableRow>
                        <TableCell colSpan={8} style={{ padding: "0px" }}>
                          <Collapse in={showForm} timeout="auto">
                            <Box>
                              {React.cloneElement(
                                inlineEditForm,
                                { row },
                                null,
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    {examinerDasboard && (
                      <TableRow
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        onClick={() => {
                          if (tableDef?.select === "row")
                            handleRowSelection(row);
                        }}
                      >
                        <TableCell
                          colSpan={6}
                          style={{
                            padding: "0px",
                          }}
                        >
                          <Collapse
                            in={selectedRowId === row?.contract}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box
                              style={{
                                width: "auto",
                                backgroundColor: theme.palette.gray20.main,
                              }}
                            >
                              <MyleaveExaminerDashboardCollapse
                                row={row}
                                role={role}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    {isMobile && !examinerDasboard && (
                      <TableRow
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        onClick={() => {
                          if (tableDef?.select === "row")
                            handleRowSelection(row);
                        }}
                      >
                        <TableCell
                          colSpan={6}
                          style={{
                            padding: "0px",
                          }}
                        >
                          <Collapse
                            in={selectedRowId === row?._id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box
                              style={{
                                width: "auto",
                                backgroundColor: theme.palette.gray20.main,
                              }}
                            >
                              <OcIsMobileTableCollapse
                                row={row}
                                myDocumentsOperator={isNotExaminer ?? false}
                                filteredTableDef={filteredTableDef}
                                handleMoreButton={handleMoreButton}
                                inlineEdit={inlineEdit ?? false}
                                onEditDocument={onEditDocument ?? (() => { })}
                                removeUpload={removeUpload ?? (() => { })}
                                isNotMyTimeExaminer={isNotExaminer ?? false}
                                matchingMyTimeitems={matchingMyTimeitems}
                                isMobile={isMobile}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    {myDocuments && (
                      <TableRow style={{ paddingBottom: 0, paddingTop: 0 }}>
                        <TableCell
                          colSpan={tableDef?.columns?.length || 5}
                          style={{
                            padding: "0px",
                          }}
                        >
                          <Collapse
                            in={!disabled && selectedRowId === row?._id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box
                              style={{
                                width: "auto",
                                backgroundColor: theme.palette.gray20.main,
                              }}
                            >
                              <MyDocumentsCollapse
                                row={row}
                                isNotExaminer={isNotExaminer ?? false}
                                removeUpload={removeUpload ?? (() => { })}
                                onEditDocument={onEditDocument ?? (() => { })}
                                removeMassUpload={removeMassUpload}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            {loading && (
              <OcDataTableLoadingRows rows={2} columns={loadingTypes} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (data?.total ?? 0) > 0 && (
        <Box id={`${tableDef.id}-pagination`}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={data.total ?? 0}
            rowsPerPage={rowsPerPage}
            page={tableData.page - 1}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={OcDataTablePaginationActions}
          />
        </Box>
      )}
    </Box>
  );
};

export default OcDataTable;
